import React, { FC, useMemo, useState } from 'react';
import Form from './Form';
import MagicFormItem, { ItemScheme } from './MagicFormItem';
import SubmitButton from '../Button/SubmitButton';
import { getDefaultData } from '../../Modules/MagicForm/Services/magicForm-defaultsData.service';
import { getValidationScheme } from '../../Modules/MagicForm/Services/magicForm-validationScheme.service';
import RemoveButton from '../Button/RemoveButton';
import { useInputValidation } from '../../Modules/Base/Hooks/useInputValidation';
import { MagicField } from '../../Enums/MagicField.enum';
import useTranslation from '../../Hooks/useTranslation';
import LinkButton from '../Button/LinkButton';
import { Theme } from '../../Enums/Theme.enum';
import MagicFormPlainTextViewerModal from '../../Modules/Dynamic/Components/MagicFormPlainTextViewer/MagicFormPlainTextViewerModal';
import { PlainTextTemplateItem } from '../../Modules/Dynamic/Components/MagicFormPlainTextViewer/MagicFormPlainTextViewer';
import { useModalContext } from '../../Modules/Modal/Services/ModalContext';
import useNotification from '../../Hooks/useNotification.hook';

interface MagicFormProps {
	formScheme: Array<ItemScheme>;
	onSubmit: (values: any) => void;
	customDefaultValues?: object;
	defaultValues?: object | undefined | null;
	submitText?: string;
	disableSubmit?: boolean;
	readOnly?: boolean;
	children?: React.ReactChild | React.ReactChild[];
	registerForChanges?: boolean;
	notMonitoredValues?: string[];
	removeButton?: {
		title?: string;
		onRemove: () => void;
	};
	plainTextViewTemplate?: PlainTextTemplateItem[];
	showCharCounter?: boolean;
	hideSubmitButton?: boolean;
}

const MagicForm: FC<MagicFormProps> = props => {
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const {
		formScheme,
		onSubmit,
		defaultValues: customDefaultValues,
		submitText = 'base.shared.save',
		disableSubmit = false,
		readOnly = false,
		removeButton,
		registerForChanges = false,
		notMonitoredValues = [],
		showCharCounter = true,
		plainTextViewTemplate,
		hideSubmitButton = false,
	} = props;

	const { getPriceInputValidation } = useInputValidation();
	const { t } = useTranslation();
	const { checkIfModalCanBeClosed } = useModalContext();
	const { showNotificationAlert } = useNotification();

	const defaultValues = useMemo(
		() => getDefaultData({ data: formScheme }),
		[formScheme],
	);
	const validationSchema = useMemo(
		() =>
			getValidationScheme(
				formScheme,
				{
					[MagicField.PRICE_WITH_CURRENCY]: getPriceInputValidation,
				},
				t,
			),
		[formScheme],
	);
	const mergedDefaultValues = customDefaultValues
		? customDefaultValues
		: defaultValues;
	const isInitialized =
		mergedDefaultValues && validationSchema && formScheme.length;

	const handleOpenPreview = () => {
		if (checkIfModalCanBeClosed()) {
			setIsPreviewOpen(true);
		} else {
			showNotificationAlert({
				content: 'component.magicForm.plainTextPreviewWarningMessage',
			});
		}
	};

	if (!isInitialized) return <></>;

	const showSubmitButton = !readOnly && !hideSubmitButton;

	if (plainTextViewTemplate) {
		notMonitoredValues.push('plainText');
	}

	return (
		<Form
			defaultValues={mergedDefaultValues}
			schema={validationSchema}
			registerForChanges={plainTextViewTemplate ? true : registerForChanges}
			notMonitoredValues={notMonitoredValues}>
			{formScheme.map((item, index) => (
				<React.Fragment key={index}>
					<MagicFormItem
						itemScheme={item}
						disabled={readOnly}
						showCharCounter={showCharCounter}
						onSubmit={onSubmit}
					/>
				</React.Fragment>
			))}

			<div
				className='d-flex flex-row mt-4 align-items-center'
				style={{ gap: '24px' }}>
				{showSubmitButton && (
					<SubmitButton
						title={submitText}
						onSubmit={onSubmit}
						disabled={disableSubmit}
					/>
				)}
				{removeButton && (
					<RemoveButton
						onClick={removeButton?.onRemove}
						title={removeButton?.title}
					/>
				)}
				{plainTextViewTemplate && (
					<LinkButton
						theme={Theme.BLUE}
						onClick={handleOpenPreview}
						title='base.textFormat'
					/>
				)}
			</div>
			{isPreviewOpen && plainTextViewTemplate && (
				<MagicFormPlainTextViewerModal
					isOpen
					formScheme={formScheme}
					template={plainTextViewTemplate}
					onClose={() => setIsPreviewOpen(false)}
					onSubmit={onSubmit}
				/>
			)}
		</Form>
	);
};

export default MagicForm;
